/**
 * Practice program of an event ("Lög æfingarinnar"): the songs to practise, chosen by the conductor
 * or an admin — single songs and whole folders / setlists, in order (pure; unit tested).
 *
 * Stored on the event as references only (events.program). Expanded per viewer into ONE ordered list
 * of entries: a folder / setlist adds its songs in its own order as they are now, so a later change
 * to it shows in the practice at once. The expanded list has the shape of a collection
 * (PracticeListDTO extends CollectionDTO) under the virtual id `p-<eventId>`, so the song page's list
 * machinery — the list's tab, the list drawer, "‹ Fyrra · 3 / 12 · Næsta ›", "Næsta lag ›" — works
 * for a practice unchanged. Shared by the server (validation, expansion) and the client (offline
 * expansion from the cached song and collection lists).
 */
import { z } from 'zod';
import type { CollectionDTO, CollectionEntryDTO } from './dto/library';

export const MAX_PROGRAM_ITEMS = 60;

const zRef = z.string().regex(/^[A-Za-z0-9-]{1,64}$/);

export const zProgramItem = z.discriminatedUnion('type', [
  z.object({ id: zRef, type: z.literal('song'), songId: zRef }).strict(),
  z.object({ id: zRef, type: z.literal('collection'), collectionId: zRef }).strict(),
]);

/** One item: a song, or a whole folder / setlist. `id` is the item's own (client) id. */
export type ProgramItem = z.infer<typeof zProgramItem>;

/** At most MAX_PROGRAM_ITEMS items, unique item ids, each folder / setlist at most once. */
export const zProgram = z
  .array(zProgramItem)
  .max(MAX_PROGRAM_ITEMS)
  .superRefine((items, ctx) => {
    const ids = new Set<string>();
    const collections = new Set<string>();
    items.forEach((it, i) => {
      if (ids.has(it.id)) ctx.addIssue({ code: 'custom', message: 'duplicate_item', path: [i, 'id'] });
      ids.add(it.id);
      if (it.type !== 'collection') return;
      if (collections.has(it.collectionId)) ctx.addIssue({ code: 'custom', message: 'duplicate_collection', path: [i, 'collectionId'] });
      collections.add(it.collectionId);
    });
  });

/**
 * The program of an event: its own (`json`, events.program), else — never set — its setlist (a
 * one-off's, or copied from its series) as the only item; [] for none. Unreadable JSON counts as none.
 */
export function eventProgram(json: string | null, setlistId: string | null): ProgramItem[] {
  if (json !== null) {
    try {
      const r = zProgram.safeParse(JSON.parse(json));
      return r.success ? r.data : [];
    } catch {
      return [];
    }
  }
  return setlistId ? [{ id: setlistId, type: 'collection', collectionId: setlistId }] : [];
}

/* ─── The expanded list ─────────────────────────────────────────────────── */

const PREFIX = 'p-';

/** The virtual list id of an event's practice (the song page's ?c=). */
export const practiceListId = (eventId: string): string => `${PREFIX}${eventId}`;

/** The event of a practice list id; null for a real folder / setlist id (those are UUIDs: never "p-…"). */
export function practiceEventId(listId: string): string | null {
  return listId.startsWith(PREFIX) && listId.length > PREFIX.length ? listId.slice(PREFIX.length) : null;
}

/** A run of the expanded entries that came from one item (for headings: "Haustferðin 26 · 5 lög"). */
export interface ProgramGroup {
  itemId: string;
  type: ProgramItem['type'];
  /** The song / folder / setlist id. */
  refId: string;
  /** Folder / setlist name (null for a song). */
  name: string | null;
  /** Index of the group's first entry, and how many entries it has (0: nothing this viewer can see). */
  first: number;
  count: number;
}

/** GET /api/events/:id/program — the practice as a list (id p-<eventId>, kind 'setlist', the event's title). */
export interface PracticeListDTO extends CollectionDTO {
  eventId: string;
  startsAt: number;
  endsAt: number;
  groups: ProgramGroup[];
}

/**
 * Entries of the program as one viewer gets them. `collections`: the folders / setlists AS THIS
 * VIEWER GETS THEM (the server already leaves out entries of songs they cannot see, and orders them);
 * `visibleSongs`: the songs this viewer may see (applied to single songs). A single song the viewer
 * cannot see leaves no trace (no entry, no group); a folder / setlist that is gone, or has nothing
 * for them, adds an empty group (folders / setlists themselves are visible to everyone). Entry ids:
 * a single song's item id; a folder / setlist's own entry ids (each appears at most once in a
 * program). Positions 0..n-1.
 */
export function expandProgram(
  items: readonly ProgramItem[],
  collections: readonly CollectionDTO[],
  visibleSongs: ReadonlySet<string>,
): { entries: CollectionEntryDTO[]; groups: ProgramGroup[] } {
  const byId = new Map(collections.map((c) => [c.id, c]));
  const entries: CollectionEntryDTO[] = [];
  const groups: ProgramGroup[] = [];
  const seen = new Set<string>();
  const push = (entryId: string, songId: string, note: string | null) => {
    if (seen.has(entryId)) return false;
    seen.add(entryId);
    entries.push({ entryId, songId, position: entries.length, note });
    return true;
  };
  for (const it of items) {
    const first = entries.length;
    if (it.type === 'song') {
      if (!visibleSongs.has(it.songId)) continue;
      push(it.id, it.songId, null);
      groups.push({ itemId: it.id, type: 'song', refId: it.songId, name: null, first, count: entries.length - first });
      continue;
    }
    const col = byId.get(it.collectionId);
    if (col) {
      const ordered = [...col.entries].sort((a, b) => a.position - b.position || (a.entryId < b.entryId ? -1 : a.entryId > b.entryId ? 1 : 0));
      for (const e of ordered) push(e.entryId, e.songId, e.note);
    }
    groups.push({ itemId: it.id, type: 'collection', refId: it.collectionId, name: col?.name ?? null, first, count: entries.length - first });
  }
  return { entries, groups };
}

/** The practice list of an event for one viewer (see expandProgram for `collections` / `visibleSongs`). */
export function practiceList(
  event: { id: string; title: string; startsAt: number; endsAt: number },
  items: readonly ProgramItem[],
  collections: readonly CollectionDTO[],
  visibleSongs: ReadonlySet<string>,
  updatedAt: number,
): PracticeListDTO {
  const { entries, groups } = expandProgram(items, collections, visibleSongs);
  return {
    id: practiceListId(event.id),
    kind: 'setlist',
    name: event.title,
    description: null,
    sort: 0,
    updatedAt,
    entries,
    eventId: event.id,
    startsAt: event.startsAt,
    endsAt: event.endsAt,
    groups,
  };
}
